import { useState, useRef, useEffect } from "react";
import moment from "moment";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

const CustomDatePicker = ({
    selected,
    onChange,
    open: openProp,
    onInputClick,
    onClickOutside,
    onSelect,
    dateFormat = "yyyy-MM-dd",
    placeholderText = "Select date",
    showIcon = false,
    icon = null,
    minDate = null,
    maxDate = null,
    wrapperClassName = "",
    name,
    id,
    className = "",
    disabled = false,
    readOnly = false,
    align = "left",
}) => {
    const alignmentClass = align === "right" ? "right-0" : "left-0";
    const [internalOpen, setInternalOpen] = useState(false);
    const [viewDate, setViewDate] = useState(selected ? moment(selected) : moment());
    const [view, setView] = useState("day"); // 'day' | 'month' | 'year'
    const [yearPageStart, setYearPageStart] = useState(
        (selected ? moment(selected) : moment()).year() - 6
    );

    const containerRef = useRef(null);

    const isControlled = openProp !== undefined;
    const isOpen = isControlled ? openProp : internalOpen;

    const setOpen = (val) => {
        if (!isControlled) setInternalOpen(val);
    };

    useEffect(() => {
        if (selected) {
            setViewDate(moment(selected));
        }
    }, [selected]);

    useEffect(() => {
        if (!isOpen) {
            setView("day");
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
                onClickOutside && onClickOutside();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen, onClickOutside]);

    const handleInputClick = (e) => {
        if (disabled || readOnly) return;
        setOpen(!isOpen);
        onInputClick && onInputClick(e);
    };

    const handleDateSelect = (day) => {
        const newDate = day.toDate();
        onChange && onChange(newDate);
        onSelect && onSelect(newDate);
        setOpen(false);
    };

    const isDayDisabled = (day) => {
        if (minDate && day.isBefore(moment(minDate), "day")) return true;
        if (maxDate && day.isAfter(moment(maxDate), "day")) return true;
        return false;
    };

    const isMonthDisabled = (monthMoment) => {
        const startOfMonth = monthMoment.clone().startOf("month");
        const endOfMonth = monthMoment.clone().endOf("month");
        if (minDate && endOfMonth.isBefore(moment(minDate), "day")) return true;
        if (maxDate && startOfMonth.isAfter(moment(maxDate), "day")) return true;
        return false;
    };

    const isYearDisabled = (year) => {
        const startOfYear = moment({ year, month: 0, day: 1 });
        const endOfYear = moment({ year, month: 11, day: 31 });
        if (minDate && endOfYear.isBefore(moment(minDate), "day")) return true;
        if (maxDate && startOfYear.isAfter(moment(maxDate), "day")) return true;
        return false;
    };

    const handlePrev = () => {
        if (view === "day") {
            setViewDate(viewDate.clone().subtract(1, "month"));
        } else if (view === "month") {
            setViewDate(viewDate.clone().subtract(1, "year"));
        } else if (view === "year") {
            setYearPageStart((prev) => prev - 12);
        }
    };

    const handleNext = () => {
        if (view === "day") {
            setViewDate(viewDate.clone().add(1, "month"));
        } else if (view === "month") {
            setViewDate(viewDate.clone().add(1, "year"));
        } else if (view === "year") {
            setYearPageStart((prev) => prev + 12);
        }
    };

    const buildCalendar = () => {
        const startOfMonth = viewDate.clone().startOf("month");
        const endOfMonth = viewDate.clone().endOf("month");
        const startDay = startOfMonth.clone().startOf("week");
        const endDay = endOfMonth.clone().endOf("week");

        const days = [];
        const cursor = startDay.clone();
        while (cursor.isSameOrBefore(endDay, "day")) {
            days.push(cursor.clone());
            cursor.add(1, "day");
        }

        const weeks = [];
        for (let i = 0; i < days.length; i += 7) {
            weeks.push(days.slice(i, i + 7));
        }
        return weeks;
    };

    const weeks = buildCalendar();
    const weekDays = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
    const monthsShort = [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
    ];

    const formattedValue = selected
        ? moment(selected).format(
            dateFormat.replace(/yyyy/g, "YYYY").replace(/MM/g, "MM").replace(/dd/g, "DD")
        )
        : "";

    /* Day View Renderer */
    const renderDayView = () => (
        <>
            <div className="grid grid-cols-7 gap-1 mb-1">
                {weekDays.map((d) => (
                    <div key={d} className="text-xs font-medium text-gray-400 text-center py-1">
                        {d}
                    </div>
                ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
                {weeks.flat().map((day, idx) => {
                    const isCurrentMonth = day.isSame(viewDate, "month");
                    const isSelected = selected && moment(selected).isSame(day, "day");
                    const isDisabled = isDayDisabled(day);

                    return (
                        <button
                            key={idx}
                            type="button"
                            disabled={isDisabled}
                            onClick={() => !isDisabled && handleDateSelect(day)}
                            className={`text-xs w-8 h-8 rounded-full flex items-center justify-center transition-colors ${!isCurrentMonth ? "text-gray-300" : ""
                                } ${isSelected
                                    ? "bg-blue-500 text-white font-semibold"
                                    : !isDisabled
                                        ? "text-gray-700 hover:bg-blue-50"
                                        : "text-gray-300 cursor-not-allowed"
                                }`}
                        >
                            {day.date()}
                        </button>
                    );
                })}
            </div>
        </>
    );

    /* Month View Renderer */
    const renderMonthView = () => (
        <div className="grid grid-cols-3 gap-2 py-2">
            {monthsShort.map((m, idx) => {
                const monthMoment = viewDate.clone().month(idx);
                const isDisabled = isMonthDisabled(monthMoment);
                const isSelected =
                    selected &&
                    moment(selected).year() === viewDate.year() &&
                    moment(selected).month() === idx;

                return (
                    <button
                        key={m}
                        type="button"
                        disabled={isDisabled}
                        onClick={() => {
                            if (isDisabled) return;
                            setViewDate(viewDate.clone().month(idx));
                            setView("day");
                        }}
                        className={`text-xs py-2.5 rounded-lg font-medium transition-colors ${isSelected
                            ? "bg-blue-500 text-white"
                            : !isDisabled
                                ? "text-gray-700 hover:bg-blue-50"
                                : "text-gray-300 cursor-not-allowed"
                            }`}
                    >
                        {m}
                    </button>
                );
            })}
        </div>
    );

    /* Year View Renderer */
    const renderYearView = () => {
        const years = [];
        for (let i = 0; i < 12; i++) {
            years.push(yearPageStart + i);
        }

        return (
            <div className="grid grid-cols-3 gap-2 py-2">
                {years.map((y) => {
                    const isDisabled = isYearDisabled(y);
                    const isSelected = selected && moment(selected).year() === y;

                    return (
                        <button
                            key={y}
                            type="button"
                            disabled={isDisabled}
                            onClick={() => {
                                if (isDisabled) return;
                                setViewDate(viewDate.clone().year(y));
                                setView("month");
                            }}
                            className={`text-xs py-2.5 rounded-lg font-medium transition-colors ${isSelected
                                ? "bg-blue-500 text-white"
                                : !isDisabled
                                    ? "text-gray-700 hover:bg-blue-50"
                                    : "text-gray-300 cursor-not-allowed"
                                }`}
                        >
                            {y}
                        </button>
                    );
                })}
            </div>
        );
    };

    return (
        <div ref={containerRef} className={`relative w-full min-w-0 ${wrapperClassName}`}>
            {/* Input Element Trigger */}
            <div
                className={`flex items-center gap-2 w-full h-10 px-2 bg-white cursor-pointer ${className}`}
                onClick={handleInputClick}
            >
                {showIcon && icon && <span className="flex-shrink-0">{icon}</span>}
                <input
                    type="text"
                    readOnly
                    disabled={disabled}
                    value={formattedValue}
                    placeholder={placeholderText}
                    name={name}
                    id={id}
                    autoComplete="off"
                    className="w-full flex-1 min-w-0 outline-none bg-transparent cursor-pointer text-sm text-gray-700 placeholder-gray-400"
                />
            </div>

            {/* Calendar Popup */}
            {isOpen && (
                <div className={`absolute ${alignmentClass} top-[calc(100%+4px)] z-[9999] bg-white border border-gray-200 rounded-2xl shadow-xl p-4 w-[280px]`}>
                    {/* // <div className="absolute left-10 right-0 top-[calc(100%+4px)] z-[9999] bg-white border border-gray-200 rounded-2xl shadow-xl p-4 w-[280px]"> */}
                    {/* Header Controls */}
                    <div className="flex items-center justify-between mb-3">
                        <button
                            type="button"
                            onClick={handlePrev}
                            className="p-1 hover:bg-gray-100 rounded-full text-gray-600"
                        >
                            <FaChevronLeft size={12} />
                        </button>

                        <div className="flex items-center gap-1.5">
                            {view === "day" && (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => setView("month")}
                                        className="text-sm font-semibold text-gray-800 hover:text-blue-600 hover:bg-blue-50 px-1.5 py-0.5 rounded transition-colors"
                                    >
                                        {viewDate.format("MMMM")}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setYearPageStart(viewDate.year() - 6);
                                            setView("year");
                                        }}
                                        className="text-sm font-semibold text-gray-800 hover:text-blue-600 hover:bg-blue-50 px-1.5 py-0.5 rounded transition-colors"
                                    >
                                        {viewDate.format("YYYY")}
                                    </button>
                                </>
                            )}

                            {view === "month" && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setYearPageStart(viewDate.year() - 6);
                                        setView("year");
                                    }}
                                    className="text-sm font-semibold text-gray-800 hover:text-blue-600 hover:bg-blue-50 px-1.5 py-0.5 rounded transition-colors"
                                >
                                    {viewDate.format("YYYY")}
                                </button>
                            )}

                            {view === "year" && (
                                <span className="text-sm font-semibold text-gray-800">
                                    {`${yearPageStart} - ${yearPageStart + 11}`}
                                </span>
                            )}
                        </div>

                        <button
                            type="button"
                            onClick={handleNext}
                            className="p-1 hover:bg-gray-100 rounded-full text-gray-600"
                        >
                            <FaChevronRight size={12} />
                        </button>
                    </div>

                    {/* View Switchers */}
                    {view === "day" && renderDayView()}
                    {view === "month" && renderMonthView()}
                    {view === "year" && renderYearView()}
                </div>
            )}
        </div>
    );
};

export default CustomDatePicker;